// src/components/RelationModal/RelationModal.js
import React, { useState, useEffect, useMemo } from 'react';
import './RelationModal.css';

const ALL_ROLES = [
  {
    value: 'PRESIDENT',
    label: 'Président',
    description: 'Dirigeant rémunéré',
    isManager: true,
    managerRole: 'president',
    forEntities: ['SAS', 'SASU'] // Only for SAS/SASU
  },
  {
    value: 'DIRECTEUR_GENERAL',
    label: 'Directeur Général',
    description: 'Dirigeant rémunéré',
    isManager: true,
    managerRole: 'directeur_general',
    forEntities: ['SAS', 'SASU'] // Only for SAS/SASU
  },
  {
    value: 'GERANT',
    label: 'Gérant',
    description: 'Dirigeant rémunéré',
    isManager: true,
    managerRole: 'gerant',
    forEntities: ['SARL', 'EURL'] // Only for SARL/EURL
  },
  {
    value: 'ASSOCIE',
    label: 'Associé simple',
    description: 'Reçoit uniquement les dividendes',
    isManager: false,
    managerRole: null,
    forEntities: null // For all entity types
  }
];

const RelationModal = ({
  relation,
  individual,
  entity,
  onConfirm,
  onClose,
  existingRelations
}) => {
  // Compute default entry date from entity's exercice period
  const getDefaultEntryDate = () => {
    // Try entity's forecast start date first
    const forecastStart = entity?.forecast_start_date || entity?.forecastStartDate;
    if (forecastStart) {
      if (typeof forecastStart === 'number') {
        return new Date(forecastStart).toISOString().split('T')[0];
      }
      if (typeof forecastStart === 'string') {
        return forecastStart.split('T')[0];
      }
    }
    // Try entity's creation date
    const creationDate = entity?.creation_date || entity?.creationDate;
    if (creationDate) {
      if (typeof creationDate === 'string') {
        return creationDate.split('T')[0];
      }
    }
    // Fallback to today
    return new Date().toISOString().split('T')[0];
  };

  // Filter roles based on entity type (SAS/SASU vs SARL/EURL)
  const entityType = entity?.entity_type || entity?.entityType || entity?.legal_form || entity?.legalForm || '';

  const availableRoles = useMemo(() => {
    return ALL_ROLES.filter(r =>
      r.forEntities === null || r.forEntities.includes(entityType.toUpperCase())
    );
  }, [entityType]);

  // Default to first manager role or ASSOCIE
  const getDefaultRole = () => {
    const managerRoles = availableRoles.filter(r => r.isManager);
    return managerRoles.length > 0 ? managerRoles[0].value : 'ASSOCIE';
  };

  const [role, setRole] = useState('ASSOCIE');
  const [percentage, setPercentage] = useState(100);
  const [entryDate, setEntryDate] = useState(getDefaultEntryDate());
  const [error, setError] = useState('');

  // Set default role when entity type is known
  useEffect(() => {
    if (availableRoles.length > 0 && !relation.role) {
      setRole(getDefaultRole());
    }
  }, [availableRoles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse entity managers to get remuneration info
  // Check exercice_data first (per-year config), then fall back to global managers
  const entityManagers = useMemo(() => {
    if (!entity) return [];
    try {
      // First, try to get managers from exercice_data (per-year config takes precedence)
      const exerciceData = entity.exercice_data || entity.exerciceData;
      if (exerciceData && typeof exerciceData === 'object') {
        // Get the first year's managers (sorted by year)
        const years = Object.keys(exerciceData).sort();
        for (const year of years) {
          const yearManagers = exerciceData[year]?.managers;
          if (yearManagers && yearManagers.length > 0) {
            return yearManagers;
          }
        }
      }

      // Fall back to global managers
      const managersStr = entity.managers || entity.managers_json;
      // Handle both string and array formats
      if (Array.isArray(managersStr)) return managersStr;
      return managersStr ? JSON.parse(managersStr) : [];
    } catch {
      return [];
    }
  }, [entity]);

  // Get remuneration info for current selected role
  const currentManagerInfo = useMemo(() => {
    const selectedRole = ALL_ROLES.find(r => r.value === role);
    if (!selectedRole?.isManager) return null;

    // Find matching manager in entity (handle both snake_case and camelCase)
    const manager = entityManagers.find(m =>
      m.role?.toLowerCase() === selectedRole.managerRole
    );

    // Support both net_monthly (from DB/wizard) and netMonthly (from API)
    const netMonthly = manager?.netMonthly || manager?.net_monthly || 0;
    const months = manager?.months || 12;
    const status = manager?.status || 'ASSIMILE_SALARIE';

    // Return manager info even if netMonthly is 0 (Non rémunéré)
    // This allows creating linked income for tracking purposes
    if (manager) {
      return {
        netMonthly,
        months,
        status,
        yearlyNet: netMonthly * months
      };
    }
    return null;
  }, [role, entityManagers]);

  useEffect(() => {
    // If editing existing relation, load values
    if (relation.role) {
      setRole(relation.role);
    }
    if (relation.percentage) {
      setPercentage(relation.percentage);
    }
    if (relation.entryDate) {
      setEntryDate(relation.entryDate.split('T')[0]);
    }
  }, [relation]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    const newPercentage = parseFloat(percentage);

    if (isNaN(newPercentage) || newPercentage <= 0 || newPercentage > 100) {
      setError('Le pourcentage doit être entre 1 et 100.');
      return;
    }

    // Check total percentage doesn't exceed 100%
    const totalPercentage = existingRelations
      .filter(rel => rel.id !== relation.id)
      .reduce((sum, rel) => sum + (rel.percentage || 0), 0);

    if (totalPercentage + newPercentage > 100) {
      setError(`Total ne peut excéder 100%. Déjà utilisé: ${totalPercentage}%`);
      return;
    }

    if (!entryDate) {
      setError('La date d\'entrée est requise.');
      return;
    }

    const selectedRole = ALL_ROLES.find(r => r.value === role);

    onConfirm({
      ...relation,
      role,
      isManager: selectedRole?.isManager || false,
      managerRole: selectedRole?.managerRole || null,
      percentage: newPercentage,
      entryDate,
      receiveDividends: true, // Toujours true - associé reçoit auto sa quote-part
      // Pass manager info for income creation
      managerInfo: currentManagerInfo,
      // Flag to update entity manager's salaryStartDate
      updateManagerSalaryStart: selectedRole?.isManager || false
    });
  };

  const individualName = individual
    ? `${individual.first_name || individual.firstName || ''} ${individual.last_name || individual.lastName || ''}`.trim() || 'Individu'
    : 'Individu';

  const entityName = entity?.entity_name || entity?.entityName || entity?.name || 'Société';

  return (
    <div className="relation-modal-overlay" onClick={onClose}>
      <div className="relation-modal" onClick={e => e.stopPropagation()}>
        <div className="relation-modal-header">
          <h3>Définir la relation</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="relation-modal-parties">
          <span className="party individual">{individualName}</span>
          <span className="arrow">↔</span>
          <span className="party entity">{entityName}</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="relation-section">
            <label className="section-label">Rôle dans la société {entityType && <span className="entity-type-badge">{entityType}</span>}</label>
            <div className="role-options">
              {availableRoles.map(r => (
                <label key={r.value} className={`role-option ${role === r.value ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={role === r.value}
                    onChange={() => setRole(r.value)}
                  />
                  <div className="role-content">
                    <span className="role-label">{r.label}</span>
                    <span className="role-description">{r.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="relation-row">
            <div className="relation-field">
              <label>Part du capital</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={percentage}
                  onChange={(e) => setPercentage(e.target.value)}
                />
                <span className="suffix">%</span>
              </div>
            </div>

            <div className="relation-field">
              <label>Date d'entrée</label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="error-message">{error}</p>}

          <div className="relation-modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-confirm">
              Confirmer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RelationModal;
